/**
 * Stripe Subscription Service
 *
 * Calls Stripe subscription endpoints via gobodo-api-promise (StripeAccountsApi).
 * Used only when shouldUseGobodo() is true.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../gobodo/gobodoClient';

export interface CreateStripeSubscriptionInput {
  pricebookIdOrKey?: string;
  customerId?: number;
  stripeCustomerId?: string;
  priceId?: string;
  quantity?: number;
  items?: Array<{ priceId: string; quantity: number }>;
  collectionMethod?: string;
  trialPeriodDays?: number;
  daysUntilDue?: number;
}

export interface CreateStripeSubscriptionResult {
  subscriptionId?: string;
  failureException?: string;
}

/**
 * Creates a Stripe subscription.
 * Supports pricebook path (pricebookIdOrKey + empty items = entire pricebook) or single price (priceId + quantity).
 * Uses customerId (graph id) or stripeCustomerId.
 */
export async function createStripeSubscription(
  input: CreateStripeSubscriptionInput
): Promise<CreateStripeSubscriptionResult> {
  const client = getGobodoClient();
  const api = new GobodoApi.StripeAccountsApi(client) as any;

  if (typeof api.createStripeSubscription !== 'function') {
    return {
      failureException:
        'createStripeSubscription is not available in the Gobodo API.',
    };
  }

  const payload: Record<string, unknown> = {};

  if (input.pricebookIdOrKey) {
    payload.pricebookIdOrKey = input.pricebookIdOrKey;
    payload.items = input.items ?? [];
  }
  if (input.customerId != null) payload.customerId = input.customerId;
  if (input.stripeCustomerId) payload.stripeCustomerId = input.stripeCustomerId;
  if (input.priceId) payload.priceId = input.priceId;
  if (input.quantity != null) payload.quantity = input.quantity;
  if (input.collectionMethod) payload.collectionMethod = input.collectionMethod;
  if (input.trialPeriodDays != null) payload.trialPeriodDays = input.trialPeriodDays;
  if (input.daysUntilDue != null) payload.daysUntilDue = input.daysUntilDue;

  try {
    const data = await api.createStripeSubscription(payload);
    const failureException =
      typeof data?.failureException === 'string'
        ? data.failureException
        : data?.getFailureException?.();
    if (failureException) return { failureException };

    const subscription = data?.subscription ?? data?.getSubscription?.();
    const subscriptionId =
      subscription?.id ?? subscription?.getId?.() ?? subscription?.subscription_id ?? subscription?.subscriptionId;

    return { subscriptionId: subscriptionId ? String(subscriptionId) : undefined };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    return { failureException: msg };
  }
}
